<template>
  <transition name="drawer-fade">
    <div v-if="isInstantiated" :class="drawerContainerClasses" :style="zIndex ? { zIndex } : {}">
      <div class="drawer_backdrop" @click="handleClose"></div>
      <div
        :class="['drawer_content', 'bg-dark', contentCSS]"
        :style="{ top: top, width: width, height: `calc(100% - ${top})` }"
      >
        <div class="drawer_header">
          <div class="flex flex-col">
            <h3 v-if="title.trim() !== ''">{{ title }}</h3>
            <slot name="subtitle"></slot>
          </div>
          <ThemeImage
            :icon="imageNames.CLOSE_GREY"
            alt="close_icon"
            class="cursor-pointer"
            @click="handleClose"
          />
        </div>
        <div class="drawer_body custom_scrollbar" style="overflow-y: scroll">
          <slot></slot>
        </div>
        <div v-if="hasFooterContent" class="drawer_footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  computed,
  useSlots,
} from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  hideSupportWidget,
  showSupportWidget,
} from "@/src/utils/supportWidget.js";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  direction: {
    type: String,
    default: "bottom",
  },
  top: {
    type: String,
    default: "0px",
  },
  width: {
    type: String,
    default: "100%",
  },
  contentCSS: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "",
  },
  zIndex: {
    type: Number,
    default: null,
  },
  hideSupportWidget: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);
const slots = useSlots();

const isVisible = ref(props.modelValue);
const isInstantiated = ref(props.modelValue);
let closeTimeoutId = null;

const hasFooterContent = computed(() => !!slots.footer);

const setViewportHeight = () => {
  if (window.visualViewport?.height <= 500) {
    window.scrollTo({ top: 0 });
    document.documentElement.style.setProperty("--drawer-max-height", "60vh");
  } else {
    document.documentElement.style.setProperty("--drawer-max-height", "90%");
  }
};

const handleClose = () => {
  emit("update:modelValue", false);
};

const drawerContainerClasses = computed(() => {
  return [
    "drawer_container",
    isVisible.value ? "open" : "closed",
    props.direction,
  ];
});

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      // Cancel any pending close timeout to prevent a stale timeout
      // from destroying the drawer after it has been reopened
      if (closeTimeoutId) {
        clearTimeout(closeTimeoutId);
        closeTimeoutId = null;
      }
      isInstantiated.value = true;
      isVisible.value = true;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";

      if (props.hideSupportWidget) hideSupportWidget();
    } else {
      isVisible.value = false;
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("position");
      document.body.style.removeProperty("width");

      if (props.hideSupportWidget) showSupportWidget();

      closeTimeoutId = setTimeout(() => {
        closeTimeoutId = null;
        // Only destroy DOM if the drawer is still supposed to be closed
        if (!props.modelValue) {
          isInstantiated.value = false;
        }
      }, 300);
    }
  },
  { immediate: true },
);

onMounted(() => {
  window.addEventListener("resize", setViewportHeight);
  window.visualViewport?.addEventListener("resize", setViewportHeight);
  setViewportHeight();
});

onBeforeUnmount(() => {
  if (closeTimeoutId) {
    clearTimeout(closeTimeoutId);
    closeTimeoutId = null;
  }
  window.removeEventListener("resize", setViewportHeight);
  window.visualViewport?.removeEventListener("resize", setViewportHeight);

  // Release if the drawer is torn down while still open, so the widget never
  // gets stuck hidden.
  if (props.hideSupportWidget && props.modelValue) showSupportWidget();
});
</script>

<style scoped>
.drawer_container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  z-index: 1000;
  visibility: hidden;
  opacity: 0;
  transition:
    visibility 0s linear 0.3s,
    opacity 0.3s linear,
    transform 0.3s ease-in-out;
}

.drawer_container.open {
  visibility: visible;
  opacity: 1;
  transition:
    visibility 0s,
    opacity 0.3s linear,
    transform 0.3s ease-in-out;
}

.drawer_backdrop {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgb(0 0 0 / 50%);
  cursor: pointer;
}

.drawer_content {
  position: absolute;
  z-index: 2;
  display: flex;
  flex-direction: column;
  height: calc(100% - 50px);
  overflow: hidden;
  border-radius: 10px 0 0 10px;
  background: var(--custom-drawer-bg);
  box-shadow: -10px 10px 100px 0 rgb(0 0 0 / 24%);
  backdrop-filter: blur(50px);
  color: var(--foreground-rgb);
}

.drawer_header {
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 24px 16px;
  border-bottom: 1px solid var(--white-opacity-12);

  font-family: "Proxima Nova", sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.drawer_body {
  flex: 1;
  overflow-y: scroll;
  padding: 24px 16px;
  /* Force scrollbar to always show on macOS */
  -webkit-overflow-scrolling: touch;
}

.drawer_footer {
  flex-shrink: 0;
  padding: 16px;
  border-top: 1px solid var(--white-opacity-12);
}

.drawer_container.top {
  align-items: flex-start;
}

.drawer_container.top .drawer_content {
  width: 100%;
  transform: translateY(-100%);
  transition: transform 0.3s ease-in-out;
}

.drawer_container.top.open .drawer_content {
  transform: translateY(0);
}

.drawer_container.top.closed .drawer_content {
  transform: translateY(-100%);
}

.drawer_container.bottom {
  align-items: flex-end;
}

.drawer_container.bottom .drawer_content {
  width: 100%;
  transform: translateY(100%);
  transition: transform 0.3s ease-in-out;
}

.drawer_container.bottom.open .drawer_content {
  transform: translateY(0);
}

.drawer_container.bottom.closed .drawer_content {
  transform: translateY(100%);
}

.drawer_container.left {
  justify-content: flex-start;
}

.drawer_container.left .drawer_content {
  height: 100%;
  transform: translateX(-100%);
  transition: transform 0.3s ease-in-out;
}

.drawer_container.left.open .drawer_content {
  transform: translateX(0);
}

.drawer_container.left.closed .drawer_content {
  transform: translateX(-100%);
}

.drawer_container.right {
  justify-content: flex-end;
}

.drawer_container.right .drawer_content {
  height: 100%;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
}

.drawer_container.right.open .drawer_content {
  transform: translateX(0);
}

.drawer_container.right.closed .drawer_content {
  transform: translateX(100%);
}

.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}
.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
  transform: translateX(0%);
}
</style>
